import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Dialog, Listbox, Transition } from "@headlessui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Check, ChevronDown, Clock, Copy, KeyRound, MailCheck, Plus, ShieldCheck, Trash2, UserCog, X } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { useAuthStore } from "@/store/auth";
import {
  inviteAgent,
  listTeamMembers,
  setMemberActive,
  setMemberRole,
  deleteMember,
  type InviteAgentResult,
  type TeamMember,
} from "@/services/team";
import { sendResetEmail } from "@/services/password";
import { useIsFullAdmin } from "@/hooks/useCurrentUser";
import type { UserRole } from "@/store/auth";
import { ROLE_LABEL } from "@/lib/roles";

export function TeamPage() {
  const me = useAuthStore((s) => s.user);
  // Role-change dropdown is restricted to admin_user (DB-side via
  // set_member_role's is_full_admin() check; UI mirrors that). route_manager
  // sees the existing role as static text.
  const canChangeRoles = useIsFullAdmin();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // Status filter — persisted per team so a reload lands the admin back on
  // the same view (Active by default for a clean table).
  const filterKey = useMemo(
    () => `myroute:team:statusFilter:v1:${me?.teamId ?? "anon"}`,
    [me?.teamId],
  );
  const [statusFilter, setStatusFilter] = useState<"active" | "inactive" | "all">(
    () => readStoredStatusFilter(filterKey),
  );
  // If teamId changes (rare — user switching contexts), re-hydrate.
  useEffect(() => {
    setStatusFilter(readStoredStatusFilter(filterKey));
  }, [filterKey]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(filterKey, statusFilter);
  }, [filterKey, statusFilter]);

  const membersQuery = useQuery({
    queryKey: ["team", "members"],
    queryFn: listTeamMembers,
    // Keep the previous list visible while a background refetch runs so
    // navigating back to /team never flashes a blank page. Realtime
    // (useRealtimeSync) handles freshness when an admin elsewhere mutates
    // app_users — so a 60s stale window here is safe.
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const allMembers = membersQuery.data ?? [];
  const activeCount = allMembers.filter((m) => m.isActive).length;
  const inactiveCount = allMembers.length - activeCount;
  const filteredMembers = allMembers.filter((m) =>
    statusFilter === "all" ? true : statusFilter === "active" ? m.isActive : !m.isActive,
  );

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => setMemberActive(id, isActive),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team", "members"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Admin-initiated password reset. Server enforces the admin gate + same-team
  // check; we still gate the button client-side for consistent UX.
  const resetMut = useMutation({
    mutationFn: (userId: string) => sendResetEmail({ userId }),
    onSuccess: (res) => toast.success(`Reset link sent to ${res.email}`),
    onError: (err: Error) => toast.error(err.message),
  });

  // Role mutation. The realtime hook will see the app_users UPDATE event +
  // call rehydrateUser on the affected member if they happen to be the
  // current viewer — so their UI flips permission gates live. For all other
  // tabs, ["team", "members"] invalidation refreshes the dropdown view.
  const roleMut = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UserRole }) => setMemberRole(userId, role),
    onSuccess: () => {
      toast.success("Role updated");
      qc.invalidateQueries({ queryKey: ["team", "members"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: (userId: string) => deleteMember(userId),
    onSuccess: () => {
      toast.success("Member deleted");
      qc.invalidateQueries({ queryKey: ["team", "members"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div>
      <PageHeader
        title="Team"
        description="Invite drivers, change roles, deactivate accounts."
        actions={
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" />
            Invite member
          </button>
        }
      />

      <div className="px-4 pb-6 sm:px-8 sm:pb-8">
        {/*
          Render tabs + table ALWAYS so navigating back to /team shows the
          last-known member list immediately. TanStack Query keeps cached
          data across mounts; gating the whole UI on isLoading meant a
          slow refetch (or a transient observer drop) would replace the
          entire page with "Loading…" until reload. The "Refreshing…"
          hint only appears when we genuinely have nothing to show.
        */}
        {membersQuery.error && !allMembers.length ? (
          <p className="text-sm text-rose-700">
            Failed to load members.{" "}
            <button
              type="button"
              className="underline hover:text-rose-900"
              onClick={() => membersQuery.refetch()}
            >
              Retry
            </button>
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <StatusFilterTabs
                value={statusFilter}
                onChange={setStatusFilter}
                counts={{ active: activeCount, inactive: inactiveCount, all: allMembers.length }}
              />
              {membersQuery.isFetching && !allMembers.length ? (
                <span className="text-xs text-slate-500">Refreshing…</span>
              ) : null}
            </div>
            <MemberTable
              members={filteredMembers}
              currentUserId={me?.id ?? null}
              canChangeRoles={canChangeRoles}
              emptyHint={
                membersQuery.isFetching && !allMembers.length
                  ? "Loading team members…"
                  : statusFilter === "inactive"
                    ? "No deactivated members."
                    : statusFilter === "active"
                      ? "No active members. Invite one to get started."
                      : "No team members yet. Invite one to get started."
              }
              onResetPassword={(m) => {
                if (window.confirm(`Email ${m.fullName} (${m.email}) a password-reset link?`)) {
                  resetMut.mutate(m.id);
                }
              }}
              onDelete={(m) => {
                if (window.confirm(`Permanently delete ${m.fullName} (${m.email})? This cannot be undone.`)) {
                  deleteMut.mutate(m.id);
                }
              }}
              onChangeRole={(m, role) => roleMut.mutate({ userId: m.id, role })}
              disabled={toggleActive.isPending || resetMut.isPending || roleMut.isPending || deleteMut.isPending}
            />
          </>
        )}
      </div>

      <InviteDialog
        open={open}
        onClose={() => setOpen(false)}
        onInvited={() => qc.invalidateQueries({ queryKey: ["team", "members"] })}
      />
    </div>
  );
}

interface MemberTableProps {
  members: TeamMember[];
  currentUserId: string | null;
  canChangeRoles: boolean;
  emptyHint: string;
  onResetPassword: (m: TeamMember) => void;
  onDelete: (m: TeamMember) => void;
  onChangeRole: (m: TeamMember, role: UserRole) => void;
  disabled: boolean;
}

function readStoredStatusFilter(key: string): "active" | "inactive" | "all" {
  if (typeof window === "undefined") return "active";
  const raw = window.localStorage.getItem(key);
  return raw === "inactive" || raw === "all" ? raw : "active";
}

function StatusFilterTabs({
  value,
  onChange,
  counts,
}: {
  value: "active" | "inactive" | "all";
  onChange: (v: "active" | "inactive" | "all") => void;
  counts: { active: number; inactive: number; all: number };
}) {
  const tabs: Array<{ key: "active" | "inactive" | "all"; label: string; count: number }> = [
    { key: "active", label: "Active", count: counts.active },
    { key: "inactive", label: "Inactive", count: counts.inactive },
    { key: "all", label: "All", count: counts.all },
  ];
  return (
    <div className="mb-3 inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
      {tabs.map((t) => {
        const active = value === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
              active
                ? "bg-brand-50 text-brand-700"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t.label}
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                active ? "bg-brand-100 text-brand-700" : "bg-slate-100 text-slate-600"
              }`}
            >
              {t.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MemberTable({ members, currentUserId, canChangeRoles, emptyHint, onResetPassword, onDelete, onChangeRole, disabled }: MemberTableProps) {
  if (members.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
        <p className="text-sm font-medium text-slate-700">{emptyHint}</p>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Role</Th>
            <Th>Status</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {members.map((m) => {
            const isMe = m.id === currentUserId;
            return (
              <tr key={m.id} className="hover:bg-slate-50">
                <Td>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{m.fullName}</span>
                    {isMe && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                        You
                      </span>
                    )}
                  </div>
                </Td>
                <Td className="text-slate-600">
                  <a href={`mailto:${m.email}`} className="text-sky-700 hover:underline">{m.email}</a>
                </Td>
                <Td>
                  {canChangeRoles && !isMe ? (
                    <select
                      value={m.role}
                      onChange={(e) => {
                        const next = e.target.value as UserRole;
                        if (next === m.role) return;
                        const verb = next === "admin_user" ? "promote to Admin"
                          : next === "route_manager" ? "set as Route Manager"
                          : "demote to Driver";
                        if (window.confirm(`${verb} for ${m.fullName}?`)) {
                          onChangeRole(m, next);
                        } else {
                          // Revert the select to current value if user cancels.
                          e.currentTarget.value = m.role;
                        }
                      }}
                      disabled={disabled}
                      className="rounded-md border-slate-300 py-1 pl-2 pr-7 text-xs shadow-sm focus:border-brand-500 focus:ring-brand-500 disabled:opacity-50"
                    >
                      <option value="admin_user">Admin</option>
                      <option value="route_manager">Route Manager</option>
                      <option value="app_user">Driver</option>
                    </select>
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        m.role === "admin_user"
                          ? "bg-brand-50 text-brand-700"
                          : m.role === "route_manager"
                            ? "bg-brand-50 text-brand-700"
                            : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {m.role === "app_user" ? <UserCog className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
                      {ROLE_LABEL[m.role]}
                    </span>
                  )}
                </Td>
                <Td>
                  <StatusBadge member={m} />
                </Td>
                <Td className="text-right">
                  {!isMe && (
                    <div className="inline-flex items-center gap-3">
                      {m.isActive && (
                        <button
                          type="button"
                          onClick={() => onResetPassword(m)}
                          disabled={disabled}
                          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800 disabled:opacity-40"
                          title="Email a password-reset link to this member"
                        >
                          <KeyRound className="h-3 w-3" />
                          Send reset
                        </button>
                      )}
                      {/* Hard delete = admin_user only (server enforces the
                          same gate in delete-member). route_manager keeps
                          Send reset + Disable but cannot remove accounts. */}
                      {canChangeRoles && (
                        <button
                          type="button"
                          onClick={() => onDelete(m)}
                          disabled={disabled}
                          className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 hover:text-rose-800 disabled:opacity-40"
                          title="Permanently delete this member"
                        >
                          <Trash2 className="h-3 w-3" />
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function Th({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <td className={`whitespace-nowrap px-4 py-3 text-sm ${className}`}>{children}</td>;
}

/**
 * Tri-state account status:
 *   * Disabled — admin flipped is_active = false (overrides everything)
 *   * Pending  — invite sent, user has never signed in (accepted_invite_at IS NULL)
 *   * Active   — has signed in at least once AND not disabled
 *
 * The first-sign-in transition is handled DB-side by migration 0025's
 * trigger on auth.users.last_sign_in_at; the realtime subscription picks
 * up the resulting app_users UPDATE and this badge flips automatically.
 */
function StatusBadge({ member }: { member: TeamMember }) {
  if (!member.isActive) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
        <X className="h-3 w-3" /> Disabled
      </span>
    );
  }
  if (!member.acceptedInviteAt) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
        <Clock className="h-3 w-3" /> Pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
      <Check className="h-3 w-3" /> Active
    </span>
  );
}

interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}

function InviteDialog({ open, onClose, onInvited }: InviteDialogProps) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  // State type must include every value the Listbox below exposes — schema enum
  // user_role has admin_user / route_manager / app_user (migrations 0001 + 0018).
  const [role, setRole] = useState<UserRole>("app_user");
  const [result, setResult] = useState<InviteAgentResult | null>(null);
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: () => inviteAgent({ email, fullName, role }),
    onSuccess: (data) => {
      setResult(data);
      onInvited();
      const verb = data.reactivated
        ? "reactivated"
        : data.reinvite_mode === "reinvite-pending"
          ? "re-invited"
          : "invited";
      toast.success(
        data.email_sent
          ? `${data.email} ${verb} — email sent`
          : `${data.email} ${verb} — copy the link to share`,
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setEmail("");
        setFullName("");
        setRole("app_user");
        setResult(null);
        setCopied(false);
        mutation.reset();
      }, 200);
      return () => clearTimeout(t);
    }
  }, [open, mutation]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;
    mutation.mutate();
  }

  async function copyLink() {
    if (!result?.invite_url) return;
    await navigator.clipboard.writeText(result.invite_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Transition show={open}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl ring-1 ring-slate-200">
              {!result ? (
                <>
                  <Dialog.Title className="text-lg font-semibold text-slate-900">Invite a team member</Dialog.Title>
                  <Dialog.Description className="mt-1 text-sm text-slate-500">
                    They’ll receive a link to set their password and join your team.
                  </Dialog.Description>
                  <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                    <label className="block">
                      <span className="text-sm font-medium text-slate-700">Full name</span>
                      <input
                        type="text"
                        required
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-slate-700">Email</span>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                      />
                    </label>
                    <div>
                      <span className="text-sm font-medium text-slate-700">Role</span>
                      <Listbox value={role} onChange={setRole}>
                        <div className="relative mt-1">
                          <Listbox.Button className="relative w-full cursor-default rounded-md border border-slate-300 bg-white py-2 pl-3 pr-10 text-left text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500">
                            <span className="block truncate">{ROLE_LABEL[role]}</span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden="true" />
                            </span>
                          </Listbox.Button>
                          <Listbox.Options
                            anchor={{ to: "bottom start", gap: 4 }}
                            className="z-[60] w-[var(--button-width)] overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
                          >
                            {([
                              { value: "app_user", label: "Driver" },
                              { value: "route_manager", label: "Route Manager" },
                              { value: "admin_user", label: "Admin" },
                            ] as const).map((opt) => (
                              <Listbox.Option
                                key={opt.value}
                                value={opt.value}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-8 pr-4 ${
                                    active ? "bg-brand-600 text-white" : "text-slate-900"
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                                      {opt.label}
                                    </span>
                                    {selected && (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                                        <Check className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </div>
                      </Listbox>
                    </div>
                    <div className="mt-6 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={mutation.isPending}
                        className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {mutation.isPending ? "Sending…" : "Send invite"}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <Dialog.Title className="text-lg font-semibold text-slate-900">
                    {result.email_sent ? "Invite sent" : "Invite ready"}
                  </Dialog.Title>
                  {result.email_sent ? (
                    <p className="mt-1 flex items-start gap-2 text-sm text-slate-600">
                      <MailCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                      <span>
                        We emailed an invite to{" "}
                        <span className="font-medium text-slate-900">{result.email}</span>.
                        If it doesn't show up, share the backup link below.
                      </span>
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-slate-500">
                      Share this link with <span className="font-medium text-slate-900">{result.email}</span>.
                      It expires after one use.
                    </p>
                  )}
                  {result.invite_url && (
                    <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
                      <p className="break-all font-mono text-xs text-slate-700">{result.invite_url}</p>
                    </div>
                  )}
                  <div className="mt-6 flex justify-end gap-2">
                    {result.invite_url && (
                      <button
                        type="button"
                        onClick={copyLink}
                        className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        {copied ? "Copied" : "Copy backup link"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                    >
                      Done
                    </button>
                  </div>
                </>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
